import React from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Container, ParentStorableData, StorableSummary} from 'constants/SampleConstants';
import * as SampleConstants from 'constants/SampleConstants';
import * as context from 'Context';
import CICODropdown from 'inventory/check-in-check-out/CICODropdown';
import PrintLabelButton from 'label-printing/PrintLabelButton';
import DeleteContainerContentsModal from 'samples/containers/DeleteContainerContentsModal';
import StorableExportButton from 'samples/storables/StorableExportButton';
import TransferModal from 'samples/transfers/TransferModal';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {ElementMouseEvent} from 'util/Types';
import AddToWorklistButton from 'worklists/AddToWorklistButton';

import * as SampleUtil from './SampleUtil';
import * as StorableActions from './StorableActions';
import ChangeParentStorableButton from './storables/ChangeParentStorableButton';

const TOOLBAR_BUTTON_STYLE = ButtonStyle.TERTIARY_GRAY;
const ACTION_ORIGIN = ActionOrigin.INVENTORY_CONTAINER_PAGE_TOOLBAR;

interface Props {
  container: Container;
  onRequestTransfer?: (e: ElementMouseEvent) => void;
  parentStorable?: StorableSummary | null;
  isUnarchivable?: boolean;
  reload?: () => void;
}

export default class ContainerToolbar extends React.Component<Props> {
  static defaultProps = {
    isWritable: false,
  };

  private handleSetParentStorable = (
    parentStorableData: ParentStorableData,
    eventGroupId: string,
    onFinish?: (errorMessage?: string) => void
  ): void => {
    context.using({analyticsMetadata: {actionOrigin: ACTION_ORIGIN, eventGroupId}}, null, () => {
      StorableActions.updateContainerWithDataMods(this.props.container.id, {parentStorableData}, onFinish);
    });
  };

  private canTransfer = (): boolean =>
    TransferUtil.isSourceContainerValidForTransfer({
      ...this.props.container,
      containedItemIds: this.props.container.contents.map(({containedItemId}) => containedItemId),
    });

  override render(): JSX.Element {
    return (
      <div className="u-flex-display">
        {!SampleUtil.isFixedPlate(this.props.parentStorable) && (
          <ChangeParentStorableButton
            buttonStyle={TOOLBAR_BUTTON_STYLE}
            isWritable={this.props.container.permissions.writable}
            onSetParentStorable={this.handleSetParentStorable}
            waitForSubmitErrors={true}
            storableSchema={this.props.container.storableSchema}
            sampleStatusWarningParams={{
              actionAttempted: 'move_location',
              actionOrigin: ACTION_ORIGIN,
              sampleIds: [this.props.container.id],
            }}
          />
        )}
        {this.props.container.permissions.writable && (
          <Tooltip title="Transfer in / out">
            <Dropdown
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
                  <Option
                    onSelect={() => {
                      legacyRunModal(TransferModal, {
                        flow: SampleConstants.TransferFlow.TRANSFER_IN,
                        actionOrigin: ACTION_ORIGIN,
                        title: 'Fill container',
                        preselectedOutputConfig: {
                          outputType: ModelTypeConstants.CONTAINER,
                          outputContainerSummaries: [this.props.container],
                          outputPlate: null,
                          outputCells: [],
                        },
                        onSuccess: this.props.reload,
                      });
                    }}
                  >
                    Fill container
                  </Option>
                  {this.props.onRequestTransfer && (
                    <Option
                      isDisabled={!this.canTransfer()}
                      onSelect={this.props.onRequestTransfer}
                      tooltipTitle={this.canTransfer() ? undefined : 'Container is empty'}
                    >
                      Transfer contents
                    </Option>
                  )}
                  <Option
                    isDisabled={this.props.container.contents.length === 0}
                    tooltipTitle={
                      this.props.container.contents.length === 0 ? 'Container is empty' : undefined
                    }
                    onSelect={() => {
                      legacyRunModal(DeleteContainerContentsModal, {
                        containerIds: [this.props.container.id],
                        actionOrigin: ACTION_ORIGIN,
                        onSuccess: this.props.reload,
                      });
                    }}
                  >
                    Remove contents
                  </Option>
                </Menu>
              }
            >
              <Button
                icon="transfer"
                isDropdown={true}
                buttonStyle={TOOLBAR_BUTTON_STYLE}
                size={ButtonSize.SMALL}
                className="u-no-wrap u-mini-spacing-left"
              />
            </Dropdown>
          </Tooltip>
        )}
        <CICODropdown
          actionSource={ACTION_ORIGIN}
          buttonStyle={TOOLBAR_BUTTON_STYLE}
          buttonClassName="u-mini-spacing-left"
          items={[this.props.container]}
        />
        <PrintLabelButton
          className="u-mini-spacing-left"
          buttonStyle={TOOLBAR_BUTTON_STYLE}
          itemIds={[this.props.container.id]}
        />
        {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
          <AddToWorklistButton
            buttonStyle={TOOLBAR_BUTTON_STYLE}
            className="u-mini-spacing-left"
            ids={[this.props.container.id]}
            refreshOptions={{refreshRecentWorklists: true}}
          />
        )}
        {(() => {
          if (SampleUtil.isFixedPlate(this.props.parentStorable)) {
            return null;
          }
          return (
            <ArchiveOrUnarchiveButton
              buttonStyle={TOOLBAR_BUTTON_STYLE}
              className="u-mini-spacing-left"
              isArchivable={Boolean(this.props.container.permissions.archivable)}
              isUnarchivable={Boolean(this.props.container.permissions.unarchivable)}
              item={this.props.container}
              onArchive={(itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => {
                ArchiveActions.bulkArchiveStorablesWithDataMods(itemIds, purpose, options, this.props.reload);
              }}
              onUnarchive={(ids: Array<string>): void => {
                context.using(
                  {
                    analyticsMetadata: {
                      actionOrigin: ACTION_ORIGIN,
                      eventGroupId: makeAnalyticsEventId(),
                    },
                  },
                  null,
                  () => {
                    ArchiveActions.bulkUnarchiveStorablesWithDataMods(ids, this.props.reload);
                  }
                );
              }}
              getAnalyticsMetadata={() => ({
                actionOrigin: ACTION_ORIGIN,
                eventGroupId: makeAnalyticsEventId(),
              })}
            />
          );
        })()}
        <StorableExportButton storableId={this.props.container.id} buttonStyle={TOOLBAR_BUTTON_STYLE} />
      </div>
    );
  }
}
